import { defineStore, getActivePinia } from "pinia"
import { ref, nextTick, computed } from "vue"
import tools from "@/utils/tools.js"
import ttsApi from "@/api/api/apiTts.js"
import sseApi from "@/api/api/apiSse.js"
import useGlobalPlayDataStore from "@/store/globalPlayDataStore.js"
import layoutConfig from "@/config/layoutConfig"
import message from "@/components/functionCallComponent/message.jsx"
import { getCreditInfo } from "@/api/api/apiCredit.js"
import apiUser from "@/api/api/apiUser.js"
import useUserDataStore from "@/store/userDataStore.js"
import { watch } from "vue"
import { openCreditDialog } from "@/page/credits/addCreditsDialog.js"
import { showActionDialog } from "@/components/functionCallComponent/action/action.js"
import { getInvitePop } from "@/api/api/apiCredit.js"
import ShowInviteDialog from "@/components/inviteDialog/inviteDialog.js"

export const useTtsHomeStore = defineStore("ttsHome", () => {
  // state
  const userDataStore = useUserDataStore()
  const searchKeyword = ref("")
  const speechTitle = ref("")
  const dialogues = ref([{ voice: {}, content: "" }])
  const generateLoading = ref(false)
  const speechProcess = ref(0)

  const ttsList = ref([])
  const lastId = ref(0)
  const free_tts_duration = ref(userDataStore.free_tts_duration ?? 0)
  const loadingMore = ref(false)
  const isLoading = ref(false)
  const loadError = ref(false)
  const finished = ref(false)

  const charCount = ref(0)

  // const lastGenerateTs = ref(0)

  const testData = ref({
    conversations: [
      {
        voice_id: 3,
        contents: [
          {
            content: "欢迎来到我们的节目。",
            voice_style: "主持人开场白",
          },
          {
            content: "今天我们要讨论的是人工智能的发展。",
            voice_style: "引入主题",
          },
        ],
      },
      {
        voice_id: 4,
        contents: [
          {
            content: "确实，AI 已经在多个领域展现出强大能力。",
            voice_style: "",
          },
          {
            content: "例如，医疗、教育、甚至艺术创作。",
            voice_style: "嘉宾发言",
          },
        ],
      },
    ],
    title: "关于人工智能的讨论",
  })

  const search = async (keyword) => {
    lastId.value = 0
    searchKeyword.value = keyword
    isLoading.value = true
    try {
      const generateList = ttsList.value.filter((item) => item.state === 2)
      const { code, data } = await ttsApi.getTtsSpeechList({
        last_id: lastId.value,
        title: keyword,
      })
      isLoading.value = false
      if (code == 200) {
        loadError.value = false
        lastId.value = data?.last_id ? data?.last_id : 0
        // ttsList.value = data?.list || []
        ttsList.value = [...generateList, ...(data?.list || []).filter((item) => item?.state !== 2)]
        addplayerList()
      } else {
        loadError.value = true
      }
    } catch (e) {
      isLoading.value = false
      loadError.value = true
    }
  }

  watch(
    () => userDataStore.free_tts_duration,
    (val) => {
      free_tts_duration.value = val ?? 0
    },
    { immediate: true }
  )

  const clearAll = () => {
    clearDialogues()
    clearData()
  }

  const clearDialogues = () => {
    dialogues.value = []
    addDialogue()
  }

  const clearData = () => {
    lastId.value = 0
    ttsList.value = []
    loadingMore.value = false
    isLoading.value = false
    loadError.value = false
    finished.value = false
  }

  const init = () => {}

  const getCredit = () => {
    apiUser.userGetInfo().then((res) => {
      if (res && res.code === 200) {
        free_tts_duration.value = res.data?.free_tts_duration ?? 0
      }
    })
  }

  const setTopVoice = (item) => {
    if (!item) return
    if (dialogues.value.length === 0) {
      dialogues.value.push({ voice: {}, content: "" })
    }
    dialogues.value[0].voice = { ...item }
  }

  /** 检查防抖 & 并发锁 */
  function guardGenerate() {
    return true
  }

  /** 根据 payload 建立/替换 loading 任务，返回 _genId 和 taskIndex getter */
  function upsertLoadingTask(payload, isRetry = false) {
    const _genId = Date.now() + Math.random()
    const task = {
      title: payload.title,
      conversations: payload.conversations,
      state: 2,
      loading: true,
      progress: 0,
      _genId,
    }

    if (isRetry && payload.id) {
      const i = ttsList.value.findIndex((t) => t.id === payload.id)
      i !== -1 ? (ttsList.value[i] = task) : ttsList.value.unshift(task)
    } else {
      ttsList.value.unshift(task)
    }

    return {
      _genId,
      taskIndex: () => ttsList.value.findIndex((t) => t._genId === _genId),
    }
  }

  /** 共用的 SSE 监听 */
  async function listenSpeechSSE(conn_id, oldData, afterDone) {
    let sseResData = {}

    await sseApi.sseGenerateTtsSpeech({
      params: `conn_id=${conn_id}`,
      onMessage: (ev) => {
        if (ev.event === "pgc_speech") sseResData = JSON.parse(ev.data || "{}")
        if (ev.event === "done") afterDone(sseResData)
      },
      onClose: () => {
        generateLoading.value = false
        if (typeof sseResData.state === "undefined" || sseResData.state !== 3) {
          const idx = ttsList.value.findIndex(
            (t) =>  t?.id == sseResData?.id
          )
          if (idx !== -1) {
            Object.assign(ttsList.value[idx], {
              ...sseResData,
              // state: 6,
              loading: false,
              progress: 100,
              // 其他状态
            })
          }
        }
      },
    })
  }

  /* ────────────────────────── 主流程 ────────────────────────── */

  async function doGenerate(payload, isRetry = false) {
    if (generateLoading.value) return
    generateLoading.value = true

    const pinia = getActivePinia()
    const { $$t } = pinia?.$i18n ?? {}

    try {
      /* 调用接口（首发 / 重试） */
      const apiRes = isRetry
        ? await ttsApi.generateTtsSpeecRetry({ id: payload.id })
        : await ttsApi.generateTtsSpeech(payload)

      const { code, data, msg } = apiRes
      if (code !== 200 || !data?.conn_id) {
        generateLoading.value = false
        if (code == 6364 || code == 6365) {
          messageError("当前输入内容违反政策，请修改后重试")
        } else if (code == 6310) {
          const is_vip = userDataStore?.userInfo?.user?.is_vip
          const Router =
            window?.$getVueCurrentInstance?.().appContext?.config?.globalProperties?.$router
          showActionDialog({
            content: $$t("create.not_enough_credits_action"),
            cancelLabel: $$t("common.cancel"),
            confirmLabel: is_vip ? $$t("create.buy_credits") : $$t("create.subscribe"),
            type: "subscribe",
            confirm: (closeAction) => {
              closeAction()
              if (is_vip) {
                openCreditDialog()
                return
              }
              Router.push({
                name: "subscribe",
                query: {
                  membership_source1: "tts",
                  membership_source2: "speech_generate",
                },
              })
            },
            cancel: (closeAction) => {
              if (userDataStore?.isLogin) {
                getInvitePop({}).then((res) => {
                  if (res.code == 200) {
                    if (res?.data?.pop) {
                      ShowInviteDialog()
                    }
                  }
                })
              }
              closeAction()
            },
          })
        } else {
          messageError(msg)
        }
        return
      }

      /* 插入 loading 行 */
      // const { _genId, taskIndex } = upsertLoadingTask(payload, isRetry)

      if (data?.id) {
        const task = {
          ...data,
          loading: true,
          progress: 0,
        }
        const i = ttsList.value.findIndex((t) => t.id === data?.id)
        i !== -1 ? (ttsList.value[i] = task) : ttsList.value.unshift(task)
      }

      /* 监听 SSE，直到 done */
      await listenSpeechSSE(data.conn_id, data, (doneData) => {
        // const i = taskIndex()
        // if (i !== -1) {
        //   const existIdx = ttsList.value.findIndex((item) => item.id && doneData.id && item.id === doneData.id)
        //   if (doneData.id && existIdx !== -1 && existIdx !== i) {
        //     // 已有同 id 项，移除 loading 行
        //     ttsList.value.splice(i, 1)
        //   } else {
        //     Object.assign(ttsList.value[i], {
        //       ...doneData,
        //       loading: false,
        //       progress: 100,
        //       // state: 3,
        //     })
        //   }
        // }

        if (doneData?.state == 8) {
          messageError("当前输入内容违反政策，请修改后重试")
        }
        const idx = ttsList.value.findIndex((t) => t?.id == doneData?.id)
        if (idx !== -1) {
          Object.assign(ttsList.value[idx], {
            ...doneData,
            loading: false,
            progress: 100,
            // 其他状态
          })
        }

        userDataStore.refreshUserAccount()
        if (free_tts_duration.value > 0) getCredit()
      })
    } catch (err) {
      generateLoading.value = false
      console.error("生成 TTS 失败:", err)
      const i = taskIndex()
      if (i !== -1) ttsList.value.splice(i, 1)
    }
  }

  /* ────────────────────────── 原入口改用主流程 ────────────────────────── */

  const generate = async () => {
    const pinia = getActivePinia()
    const { $$t } = pinia?.$i18n ?? {}

    // 1. 组装 conversations
    const conversations = dialogues.value
      .map((d) => {
        const div = document.createElement("div")
        div.innerHTML = d.content || ""
        return { voice_id: d.voice?.id, contents: collectContents(div, []) }
      })
      .filter((c) => c.contents?.length)

    if (!conversations.length) {
      messageError($$t("tts.enter_your_text"))
      return
    }

    /* ========= 2. 统计指标 ========= */
    // 配音段数量
    const voice_count = conversations.length ?? 0

    // 所有文本的总字符数
    const text_length = conversations.reduce(
      (sum, c) =>
        sum + c.contents.reduce((innerSum, item) => innerSum + (item.content?.length || 0), 0),
      0
    )

    // 有 voice_style 的段落数量
    const voice_style_count = conversations.reduce(
      (sum, c) =>
        sum + c.contents.reduce((innerSum, item) => innerSum + (item.voice_style ? 1 : 0), 0),
      0
    )

    const globalProperties = window?.$getVueCurrentInstance?.().appContext.config.globalProperties
    globalProperties.logCustomEvent("speech_generate_click", {
      voice_count,
      text_length,
      voice_style_count,
    })

    await doGenerate({ title: speechTitle.value, conversations })
  }

  const speechRetry = async (origin) => {
    // origin: { id, title, conversations }
    await doGenerate(origin, true)
  }

  const speechDelete = async (data) => {
    const { code } = await ttsApi.sendSpeechDelete({ id: data?.id })
    if (code == 200) {
      const index = ttsList.value.findIndex((item) => item.id === data?.id)
      if (index !== -1) {
        ttsList.value.splice(index, 1)
      }
    }
  }

  function collectContents(el, arr = []) {
    el.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const txt = node.textContent?.trim()
        if (txt) arr.push({ content: txt, voice_style: "" })
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const elem = node
        if (elem.tagName === "SPAN" && elem.classList.contains("desc-highlight")) {
          arr.push({
            content: elem.textContent || "",
            voice_style: elem.getAttribute("data-desc") || "",
          })
        } else {
          collectContents(elem, arr)
        }
      }
    })
    return arr
  }

  const messageError = (msg) => {
    message.error({
      maskClosable: true,
      position: "top",
      content: msg,
    })
  }

  const addplayerList = () => {
    const useData = ttsList.value
    useData.forEach((item) => {
      if (item && item.id !== undefined) {
        item.song_id = item.id
      }
    })
    console.log("useDatauseData", useData)
    const globalPlayDataStore = useGlobalPlayDataStore()
    globalPlayDataStore.setCurPlayGroup("tts_Speech_List")
    globalPlayDataStore.playlist = []
    globalPlayDataStore.addPlayDatas(useData)
  }

  const togglePlay = (item) => {
    const useData = ttsList.value
    useData.forEach((item) => {
      if (item && item.id !== undefined) {
        item.song_id = item.id
      }
    })
    const globalPlayDataStore = useGlobalPlayDataStore()
    globalPlayDataStore.setCurPlayGroup("tts_Speech_List")
    globalPlayDataStore.setPlayDatas(useData, item)
  }

  const loadMore = async (done, query) => {
    if (isLoading.value) return
    isLoading.value = true
    try {
      const params = {
        last_id: lastId.value,
      }
      if (searchKeyword.value) {
        params.title = searchKeyword.value
      }
      const { code, data } = await ttsApi.getTtsSpeechList(params)
      isLoading.value = false
      if (code == 200) {
        loadError.value = false
        lastId.value = data?.last_id ? data?.last_id : 0
        if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
          finished.value = true
          done(ttsList.value, true)
          return
        }
        const existedIds = new Set(ttsList.value.map((d) => d.id)) // 已有 id
        const newList = (data?.list || []).filter((item) => !existedIds.has(item.id))

        ttsList.value = [...ttsList.value, ...newList]
        console.log("获取音频列表：", newList, ttsList.value)
        addplayerList()
        done(ttsList.value, false)
      } else {
        loadError.value = true
        done(ttsList.value, true)
      }
    } catch (e) {
      isLoading.value = false
      loadError.value = true
      done(ttsList.value, true)
    }
  }

  const refresh = async (done, query) => {
    const generateList = ttsList.value.filter((item) => item.state === 2)
    lastId.value = 0
    isLoading.value = true
    try {
      // 这里可以调用 API 获取数据
      const { code, data } = await ttsApi.getTtsSpeechList({
        last_id: lastId.value,
      })
      isLoading.value = false
      if (code == 200) {
        loadError.value = false
        lastId.value = data?.last_id ? data?.last_id : 0
        
        // 获取 generateList 中所有 id
        const generateIds = new Set(generateList.map(item => item.id))
        
        // 过滤掉与 generateList 中 id 重复的数据
        const filteredList = (data?.list || []).filter((item) => {
          return item?.state !== 2 && !generateIds.has(item.id)
        })
        
        ttsList.value = [...generateList, ...filteredList]
        // Array.isArray(res.data?.list) ? ttsList.value.concat(res.data?.list) : ttsList.value
        console.log("获取音频列表：", data?.list, ttsList.value)
        addplayerList()
        done(ttsList.value, false)
      } else {
        loadError.value = true
        done(ttsList.value, true)
      }
    } catch (e) {
      isLoading.value = false
      loadError.value = true
      done(ttsList.value, true)
    }
  }

  const doRetry = async () => {
    isLoading.value = true
    lastId.value = 0
    try {
      // 这里可以调用 API 获取数据
      const { code, data } = await ttsApi.getTtsSpeechList({
        last_id: lastId.value,
      })
      isLoading.value = false
      if (code == 200) {
        loadError.value = false
        if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
          finished.value = true
          return
        }
        lastId.value = data?.last_id ? data?.last_id : 0
        ttsList.value = data?.list || []
        // (data?.list || []).filter((item) => item?.state !== 2)
        // Array.isArray(res.data?.list) ? ttsList.value.concat(res.data?.list) : ttsList.value
        console.log("获取音频列表：", ttsList.value)
        if (Array.isArray(data?.list)) {
          data.list.forEach((item) => {
            if (item.conn_id) {
              generateLoading.value = true
              listenSpeechSSE(item.conn_id, item, (doneData) => {
                const idx = ttsList.value.findIndex((t) => t.id === item.id)
                if (idx !== -1) {
                  Object.assign(ttsList.value[idx], {
                    ...doneData,
                    loading: false,
                    progress: 100,
                    // 其他状态
                  })
                }
                userDataStore.refreshUserAccount()
                if (free_tts_duration.value > 0) getCredit()
              })
            }
          })
        }
        addplayerList()
      } else {
        loadError.value = true
      }
    } catch (e) {
      isLoading.value = false
      loadError.value = true
    }
  }

  // 进度开始
  const startSpeechProcess = () => {
    speechProcess.value = 0
    let progress = 0
    let interval = null
    let step = 3.5
    interval = setInterval(() => {
      step = Math.max(0.5, step * 0.96) // 步长递减
      progress += step
      if (progress >= 99) {
        speechProcess.value = 99
        clearInterval(interval)
      } else {
        speechProcess.value = Math.floor(progress)
      }
    }, 200)
  }

  // 进度结束
  const endSpeechProcess = () => {
    speechProcess.value = 100
  }

  // dialogues 操作
  function setDialogues(newDialogues) {
    dialogues.value = newDialogues
  }

  function addDialogue(item) {
    dialogues.value.push(item ? item : { content: "" })
  }

  function updateDialogueContent(idx, content) {
    if (dialogues.value[idx]) {
      dialogues.value[idx].content = content
    }
  }

  // getters
  const ttsCount = computed(() => ttsList.value.length)

  return {
    ttsList,
    ttsCount,
    loadingMore,
    isLoading,
    speechProcess,
    dialogues,
    generateLoading,
    searchKeyword,
    speechTitle,
    free_tts_duration,
    finished,
    charCount,

    // 列表相关
    search,
    generate,
    loadMore,
    refresh,
    doRetry,
    togglePlay,
    startSpeechProcess,
    endSpeechProcess,
    init,
    clearData,
    clearAll,
    speechDelete,
    speechRetry,
    getCredit,
    // dialogues相关
    setDialogues,
    addDialogue,
    updateDialogueContent,
    clearDialogues,
    setTopVoice,
  }
})
